import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { getUpdate, type UpdateWithMedia } from "../api/updates";
import appStyles from "../styles/AppClasses.module.scss";
import styles from "./UpdateDetailPage.stylesheet.module.scss";

export const UpdateDetailPage = () => {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const [update, setUpdate] = useState<UpdateWithMedia | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!idOrSlug) return;
    const loadUpdate = async () => {
      const nextUpdate = await getUpdate(idOrSlug);
      setUpdate(nextUpdate);
      setLoading(false);
    };

    loadUpdate();
  }, [idOrSlug]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading)
    return (
      <div className={appStyles.appContainer}>
        <div
          className={classNames(appStyles.landing, appStyles.timelineLanding)}
        >
          <div className={appStyles.timelineLoadingWrap}>
            <div className={appStyles.spinner} aria-hidden />
            <span className={appStyles.timelineLoadingText}>Loading…</span>
          </div>
        </div>
      </div>
    );
  if (!update)
    return (
      <div className={appStyles.appContainer}>
        <div
          className={classNames(appStyles.landing, appStyles.timelineLanding)}
        >
          <p className={appStyles.timelineEmpty}>Update not found.</p>
          <Link to="/updates" className={styles.timelineBack}>
            ← Updates
          </Link>
        </div>
      </div>
    );

  const getMediaTarget = (media: { name?: string; url?: string }) => {
    const raw = `${media.name ?? ""} ${media.url ?? ""}`;
    return decodeURIComponent(raw);
  };
  const isVideo = (media: { name?: string; url?: string; type?: string }) => {
    const target = getMediaTarget(media);
    return (
      /^video\//i.test(media.type ?? "") ||
      /\.(mp4|webm|mov|m4v)(?:$|[?#\s])/i.test(target)
    );
  };
  const isImage = (media: { name?: string; url?: string; type?: string }) => {
    const target = getMediaTarget(media);
    return (
      /^image\//i.test(media.type ?? "") ||
      /\.(jpe?g|png|gif|webp|avif)(?:$|[?#\s])/i.test(target)
    );
  };

  return (
    <div className={appStyles.appContainer}>
      <div className={classNames(appStyles.landing, appStyles.timelineLanding)}>
        <article className={styles.timelineDetail}>
          <h1 className={styles.timelineDetailTitle}>{update.title}</h1>
          <time
            className={styles.timelineDetailDate}
            dateTime={update.published_at}
          >
            {formatDate(update.published_at)}
          </time>
          <div
            className={styles.timelineDetailBody}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(update.body_html, {
                ADD_ATTR: ["target"],
                ADD_TAGS: ["iframe"],
              }),
            }}
          />
          {update.media.length > 0 && (
            <section className={styles.timelineDetailMedia}>
              <h2 className={styles.timelineDetailMediaTitle}>Attachments</h2>
              <ul className={styles.timelineDetailMediaList}>
                {update.media.map((m) => (
                  <li key={m.id} className={styles.timelineDetailMediaItem}>
                    {isVideo(m) ? (
                      <video
                        src={m.url}
                        controls
                        className={styles.timelineDetailVideo}
                      />
                    ) : isImage(m) ? (
                      <a
                        href={m.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.timelineDetailImageLink}
                      >
                        <img
                          src={m.url}
                          alt={m.name}
                          className={styles.timelineDetailImage}
                        />
                      </a>
                    ) : (
                      <a
                        href={m.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.timelineDetailAttachment}
                      >
                        {m.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </article>
      </div>
    </div>
  );
};
